import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  Alert, Badge, Box, Button, CircularProgress, FormControlLabel, Grid, IconButton, InputAdornment, Paper, Snackbar, Stack, Switch,
  Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon, ContentCopy as CopyIcon, DarkMode as DarkIcon, Download as DownloadIcon, LightMode as LightIcon,
  OpenInNew as OpenIcon, Print as PrintIcon, Refresh as RefreshIcon, Check as CheckIcon,
} from '@mui/icons-material';
import MenuService from '../../services/menu.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import MenuDishes from './MenuDishes';
import MenuOrders from './MenuOrders';
import MenuImagePicker, { emptyImage } from './MenuImagePicker';
import { ACCENTS, apiError } from './menuUtils';

const FIELDS = ['name', 'tagline', 'about', 'theme', 'font', 'accent_color', 'phone', 'whatsapp', 'email', 'address', 'maps_url', 'hours', 'instagram', 'facebook', 'website', 'whatsapp_orders'];
const FONT_SAMPLES = { slab: '"Zilla Slab", Georgia, serif', serif: '"Newsreader", Georgia, serif', sans: '"Instrument Sans", system-ui, sans-serif' };

const toForm = (m) => Object.fromEntries(FIELDS.map((f) => [f, m[f] ?? (f === 'whatsapp_orders' ? false : '')]));

/** Phone-sized live preview of the public page (signed preview link, works before publishing). */
const PhonePreview = ({ url, version, t }) => (
  <Box sx={{ position: 'sticky', top: 88 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
      <Typography variant="overline" color="text.secondary">{t('menus.livePreview')}</Typography>
      <Tooltip title={t('menus.openPreview')}>
        <IconButton size="small" component="a" href={url} target="_blank" rel="noopener"><OpenIcon fontSize="small" /></IconButton>
      </Tooltip>
    </Stack>
    <Box sx={{ mx: 'auto', width: 340, height: 680, borderRadius: '40px', bgcolor: '#111', p: '12px' }}>
      <Box component="iframe" key={version} src={url} title={t('menus.livePreview')}
        sx={{ width: '100%', height: '100%', border: 0, borderRadius: '30px', bgcolor: '#fff', display: 'block' }} />
    </Box>
  </Box>
);

const MenuEditor = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('menu.edit');
  const canManage = hasPermission('menu.manage');
  const canOrders = hasPermission('menu.orders');

  const [menu, setMenu] = useState(null);
  const [form, setForm] = useState(null);
  const [slug, setSlug] = useState('');
  const [logo, setLogo] = useState(emptyImage());
  const [cover, setCover] = useState(emptyImage());
  // ?tab=orders opens a tab directly (the home dashboard links to it).
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => (['dishes', 'design', 'info', 'orders', 'share'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'dishes'));
  const [saving, setSaving] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [qr, setQr] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  const reload = useCallback(async () => {
    try {
      const res = await MenuService.getMenu(uuid);
      const m = res.data.data;
      setMenu(m);
      setForm(toForm(m));
      setSlug(m.slug);
      setLogo(emptyImage(m.logo_url));
      setCover(emptyImage(m.cover_url));
      setPreviewVersion((v) => v + 1);
    } catch (error) {
      notify(apiError(error, t('menus.errorLoading')), 'error');
    }
  }, [uuid, t, notify]);

  useEffect(() => { reload(); }, [reload]);

  const setPendingTotal = useCallback((n) => setMenu((m) => (m && m.pending_orders !== n ? { ...m, pending_orders: n } : m)), []);

  // Keep the Pedidos badge current while staff work in other tabs (the Pedidos tab refreshes it itself).
  useEffect(() => {
    if (!canOrders || tab === 'orders') return undefined;
    const id = setInterval(() => {
      MenuService.getOrdersReport(uuid).then((res) => setPendingTotal(res.data.data.pending_total)).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [canOrders, tab, uuid, setPendingTotal]);

  // The three public-menu typefaces, so the font picker shows the real thing.
  useEffect(() => {
    const id = 'menu-font-samples';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Zilla+Slab:wght@600&family=Newsreader:opsz,wght@6..72,600&family=Instrument+Sans:wght@600&display=swap';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (!menu?.public_url) return;
    QRCode.toDataURL(menu.public_url, { width: 1024, margin: 2, errorCorrectionLevel: 'M', color: { dark: '#111111', light: '#ffffff' } })
      .then(setQr).catch(() => setQr(null));
  }, [menu?.public_url]);

  const dirty = useMemo(() => {
    if (!menu || !form) return false;
    return FIELDS.some((f) => (form[f] ?? '') !== (menu[f] ?? (f === 'whatsapp_orders' ? false : ''))) || logo.file || cover.file
      || (logo.removed && logo.url) || (cover.removed && cover.url);
  }, [menu, form, logo, cover]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.name.trim()) { notify(t('common.snackbar.pleaseFillAllRequiredFields'), 'error'); return; }
    try {
      setSaving(true);
      const payload = Object.fromEntries(FIELDS.map((f) => [f, typeof form[f] === 'string' ? (form[f].trim() || (f === 'name' ? form[f] : null)) : form[f]]));
      await MenuService.updateMenu(uuid, payload);
      for (const [kind, img] of [['logo', logo], ['cover', cover]]) {
        if (img.file) await MenuService.uploadMenuImage(uuid, kind, img.file);
        else if (img.removed && img.url) await MenuService.removeMenuImage(uuid, kind);
      }
      await reload();
      notify(t('menus.saved'));
    } catch (error) {
      notify(apiError(error, t('menus.errorSaving')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async () => {
    try {
      const res = await MenuService.updateMenu(uuid, { published: !menu.published });
      setMenu((m) => ({ ...m, published: res.data.data.published }));
      notify(res.data.data.published ? t('menus.nowPublished') : t('menus.nowUnpublished'));
    } catch (error) {
      notify(apiError(error, t('menus.errorSaving')), 'error');
    }
  };

  const saveSlug = async () => {
    try {
      await MenuService.updateMenu(uuid, { slug: slug.trim().toLowerCase() });
      await reload();
      notify(t('menus.linkUpdated'));
    } catch (error) {
      notify(apiError(error, t('menus.errorSaving')), 'error');
    }
  };

  const removeMenu = async () => {
    if (!window.confirm(t('menus.deleteMenuConfirm', { name: menu.name }))) return;
    try {
      await MenuService.deleteMenu(uuid);
      navigate('/menus');
    } catch (error) {
      notify(apiError(error, t('menus.errorSaving')), 'error');
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); notify(t('menus.linkCopied')); } catch { notify(text, 'info'); }
  };

  // A printable table card: name, QR and a short instruction. Built from known values, escaped.
  const printCard = () => {
    const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const w = window.open('', '_blank', 'width=720,height=900');
    if (!w) return;
    const stripe = Array.from({ length: 16 }).map((_, i) => `<i style="background:${i % 2 ? '#fff' : esc(menu.accent_color)}"></i>`).join('');
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(menu.name)} · QR</title>
      <style>@page{size:A5;margin:0}body{margin:0;font-family:Georgia,serif;color:#111}
      .card{width:148mm;height:210mm;box-sizing:border-box;display:flex;flex-direction:column;align-items:center;text-align:center}
      .stripe{display:flex;width:100%;height:14mm}.stripe i{flex:1}
      h1{font-size:30pt;line-height:1;margin:16mm 12mm 4mm;letter-spacing:-.02em}p{font-size:13pt;margin:0 14mm}
      img{width:92mm;height:92mm;margin:10mm 0 6mm}.url{font-family:system-ui,sans-serif;font-size:10pt;color:#555}</style></head>
      <body><div class="card"><div class="stripe">${stripe}</div><h1>${esc(menu.name)}</h1><p>${esc(t('menus.printScan'))}</p>
      <img src="${qr}" alt=""><div class="url">${esc(menu.public_url.replace(/^https?:\/\//, ''))}</div></div>
      <script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  if (!menu || !form) {
    return <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>;
  }

  const showPreview = tab === 'design' || tab === 'info';

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2} flexWrap="wrap" useFlexGap>
        <IconButton onClick={() => navigate('/menus')} aria-label={t('menus.backToMenus')}><BackIcon /></IconButton>
        <Box sx={{ width: 12, height: 40, borderRadius: 1, bgcolor: menu.accent_color, flex: 'none' }} />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h4" fontWeight="bold" noWrap>{menu.name}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{menu.public_url.replace(/^https?:\/\//, '')} · {t('menus.views', { count: menu.view_count || 0 })}</Typography>
        </Box>
        <Button variant="outlined" startIcon={<OpenIcon />} href={menu.published ? menu.public_url : menu.preview_url} target="_blank" rel="noopener">
          {menu.published ? t('menus.openMenu') : t('menus.preview')}
        </Button>
        <Paper variant="outlined" sx={{ pl: 2, pr: 1, py: 0.5, borderRadius: 2, borderColor: menu.published ? 'success.main' : 'divider' }}>
          <FormControlLabel labelPlacement="start" disabled={!canManage}
            control={<Switch color="success" checked={!!menu.published} onChange={togglePublished} />}
            label={<Typography fontWeight={600} color={menu.published ? 'success.main' : 'text.secondary'}>{menu.published ? t('menus.published') : t('menus.draft')}</Typography>} />
        </Paper>
      </Stack>

      {!menu.published && (
        <Alert severity="info" sx={{ mb: 2 }}>{canManage ? t('menus.draftHint') : t('menus.draftHintReadOnly')}</Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }} variant="scrollable" allowScrollButtonsMobile>
        <Tab value="dishes" label={t('menus.tabDishes')} />
        <Tab value="design" label={t('menus.tabDesign')} />
        <Tab value="info" label={t('menus.tabInfo')} />
        {canOrders && (
          <Tab value="orders" label={
            <Badge color="warning" badgeContent={menu.pending_orders || 0} sx={{ alignItems: 'center', '& .MuiBadge-badge': { position: 'static', transform: 'none', ml: 1 } }}>{t('menus.tabOrders')}</Badge>
          } />
        )}
        <Tab value="share" label={t('menus.tabShare')} />
      </Tabs>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, lg: showPreview ? 7 : 12, xl: showPreview ? 8 : 12 }}>
          {tab === 'dishes' && (
            <MenuDishes menu={menu} setMenu={setMenu} reload={reload} canEdit={canEdit} notify={notify} t={t} />
          )}

          {tab === 'design' && (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700}>{t('menus.photos')}</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>{t('menus.photosHint')}</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="subtitle2" gutterBottom>{t('menus.cover')}</Typography>
                    <MenuImagePicker value={cover} onChange={setCover} disabled={!canManage || saving} t={t} height={220} hint={t('menus.coverHint')} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="subtitle2" gutterBottom>{t('menus.logo')}</Typography>
                    <MenuImagePicker value={logo} onChange={setLogo} disabled={!canManage || saving} t={t} height={220} fit="contain" hint={t('menus.logoHint')} />
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700}>{t('menus.color')}</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>{t('menus.colorHint')}</Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                  {ACCENTS.map((c) => (
                    <Box key={c} component="button" type="button" disabled={!canManage} onClick={() => set({ accent_color: c })} aria-label={c}
                      sx={{ width: 40, height: 40, borderRadius: '50%', border: 0, bgcolor: c, cursor: canManage ? 'pointer' : 'default', display: 'grid', placeItems: 'center', color: '#fff',
                        outline: form.accent_color === c ? '3px solid' : 'none', outlineColor: 'text.primary', outlineOffset: 3, transition: 'transform .15s', '&:hover': { transform: canManage ? 'scale(1.08)' : 'none' } }}>
                      {form.accent_color === c && <CheckIcon fontSize="small" />}
                    </Box>
                  ))}
                  <TextField size="small" label={t('menus.customColor')} value={form.accent_color} disabled={!canManage} sx={{ width: 170 }}
                    onChange={(e) => set({ accent_color: e.target.value })}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start">
                        <Box component="input" type="color" value={/^#[0-9a-f]{6}$/i.test(form.accent_color) ? form.accent_color : '#000000'} disabled={!canManage}
                          onChange={(e) => set({ accent_color: e.target.value })} aria-label={t('menus.customColor')}
                          sx={{ width: 28, height: 28, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }} />
                      </InputAdornment>
                    ) }} />
                </Stack>
                {/* The stripe diners see at the top of the menu */}
                <Box sx={{ display: 'flex', height: 28, mt: 2.5, borderRadius: 1.5, overflow: 'hidden', bgcolor: form.theme === 'dark' ? '#141414' : '#fff', border: 1, borderColor: 'divider' }}>
                  {Array.from({ length: 30 }).map((_, i) => <Box key={i} sx={{ flex: 1, bgcolor: i % 2 ? 'transparent' : form.accent_color }} />)}
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={2}>{t('menus.style')}</Typography>
                <Typography variant="subtitle2" gutterBottom>{t('menus.theme')}</Typography>
                <ToggleButtonGroup exclusive value={form.theme} disabled={!canManage} onChange={(_, v) => v && set({ theme: v })} sx={{ mb: 3 }}>
                  <ToggleButton value="light" sx={{ px: 3 }}><LightIcon sx={{ mr: 1 }} />{t('menus.themeLight')}</ToggleButton>
                  <ToggleButton value="dark" sx={{ px: 3 }}><DarkIcon sx={{ mr: 1 }} />{t('menus.themeDark')}</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="subtitle2" gutterBottom>{t('menus.font')}</Typography>
                <Grid container spacing={1.5}>
                  {['slab', 'serif', 'sans'].map((f) => (
                    <Grid key={f} size={{ xs: 12, sm: 4 }}>
                      <Paper component="button" type="button" variant="outlined" disabled={!canManage} onClick={() => set({ font: f })}
                        sx={{ width: '100%', p: 2, textAlign: 'left', cursor: canManage ? 'pointer' : 'default', borderRadius: 2, borderWidth: 2, bgcolor: 'background.paper', color: 'text.primary',
                          borderColor: form.font === f ? 'primary.main' : 'divider' }}>
                        <Typography sx={{ fontFamily: FONT_SAMPLES[f], fontSize: 28, fontWeight: 600, lineHeight: 1.1 }}>Aa Menú</Typography>
                        <Typography variant="body2" fontWeight={600} mt={1}>{t(`menus.font_${f}`)}</Typography>
                        <Typography variant="caption" color="text.secondary">{t(`menus.font_${f}_hint`)}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Stack>
          )}

          {tab === 'info' && (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={2}>{t('menus.restaurant')}</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth required label={t('menus.restaurantName')} value={form.name} disabled={!canManage} inputProps={{ maxLength: 120 }} onChange={(e) => set({ name: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth label={t('menus.tagline')} placeholder={t('menus.taglinePlaceholder')} value={form.tagline} disabled={!canManage} inputProps={{ maxLength: 160 }}
                      helperText={`${(form.tagline || '').length}/160`} onChange={(e) => set({ tagline: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth multiline minRows={4} label={t('menus.about')} placeholder={t('menus.aboutPlaceholder')} value={form.about} disabled={!canManage}
                      inputProps={{ maxLength: 3000 }} helperText={t('menus.aboutHint')} onChange={(e) => set({ about: e.target.value })} />
                  </Grid>
                </Grid>
              </Paper>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={2}>{t('menus.contact')}</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={t('menus.phone')} value={form.phone} disabled={!canManage} onChange={(e) => set({ phone: e.target.value })} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="WhatsApp" value={form.whatsapp} disabled={!canManage} helperText={t('menus.whatsappHint')} onChange={(e) => set({ whatsapp: e.target.value })} /></Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel disabled={!canManage || !form.whatsapp}
                      control={<Switch checked={!!form.whatsapp_orders} onChange={(e) => set({ whatsapp_orders: e.target.checked })} />}
                      label={<Box><Typography variant="body2" fontWeight={600}>{t('menus.whatsappOrders')}</Typography><Typography variant="caption" color="text.secondary">{t('menus.whatsappOrdersHint')}</Typography></Box>} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={t('menus.email')} type="email" value={form.email} disabled={!canManage} onChange={(e) => set({ email: e.target.value })} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={t('menus.address')} value={form.address} disabled={!canManage} onChange={(e) => set({ address: e.target.value })} /></Grid>
                  <Grid size={{ xs: 12 }}><TextField fullWidth label={t('menus.mapsUrl')} value={form.maps_url} disabled={!canManage} helperText={t('menus.mapsUrlHint')} onChange={(e) => set({ maps_url: e.target.value })} /></Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth multiline minRows={2} label={t('menus.hours')} placeholder={t('menus.hoursPlaceholder')} value={form.hours} disabled={!canManage} inputProps={{ maxLength: 500 }} onChange={(e) => set({ hours: e.target.value })} />
                  </Grid>
                </Grid>
              </Paper>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={2}>{t('menus.social')}</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField fullWidth label="Instagram" value={form.instagram} disabled={!canManage} InputProps={{ startAdornment: <InputAdornment position="start">@</InputAdornment> }} onChange={(e) => set({ instagram: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label="Facebook" placeholder="https://facebook.com/…" value={form.facebook} disabled={!canManage} onChange={(e) => set({ facebook: e.target.value })} /></Grid>
                  <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label={t('menus.website')} placeholder="https://…" value={form.website} disabled={!canManage} onChange={(e) => set({ website: e.target.value })} /></Grid>
                </Grid>
              </Paper>
            </Stack>
          )}

          {tab === 'orders' && canOrders && (
            <MenuOrders menu={menu} onPendingTotal={setPendingTotal} notify={notify} t={t} />
          )}

          {tab === 'share' && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Stack spacing={3}>
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={700}>{t('menus.publicLink')}</Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>{t('menus.publicLinkHint')}</Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField fullWidth value={menu.public_url} InputProps={{ readOnly: true }} size="small" />
                      <Button variant="contained" startIcon={<CopyIcon />} onClick={() => copy(menu.public_url)}>{t('menus.copy')}</Button>
                    </Stack>
                    {canManage && (
                      <Stack direction="row" spacing={1} mt={3} alignItems="flex-start">
                        <TextField fullWidth size="small" label={t('menus.slug')} value={slug} helperText={t('menus.slugHint')}
                          onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                          InputProps={{ startAdornment: <InputAdornment position="start">/m/</InputAdornment> }} />
                        <Button variant="outlined" onClick={saveSlug} disabled={!slug || slug === menu.slug}>{t('common.save')}</Button>
                      </Stack>
                    )}
                    {canManage && slug !== menu.slug && <Alert severity="warning" sx={{ mt: 2 }}>{t('menus.slugWarning')}</Alert>}
                  </Paper>
                  {canManage && (
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: 'error.light' }}>
                      <Typography variant="h6" fontWeight={700}>{t('menus.deleteMenu')}</Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>{t('menus.deleteMenuHint')}</Typography>
                      <Button color="error" variant="outlined" onClick={removeMenu}>{t('menus.deleteMenu')}</Button>
                    </Paper>
                  )}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={700}>{t('menus.qrTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>{t('menus.qrHint')}</Typography>
                  {qr ? <Box component="img" src={qr} alt={t('menus.qrTitle')} sx={{ width: '100%', maxWidth: 280, mx: 'auto', display: 'block' }} /> : <CircularProgress />}
                  <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
                    <Button variant="contained" startIcon={<DownloadIcon />} component="a" href={qr || undefined} download={`menu-${menu.slug}-qr.png`} disabled={!qr}>{t('menus.downloadQr')}</Button>
                    <Button variant="outlined" startIcon={<PrintIcon />} onClick={printCard} disabled={!qr}>{t('menus.printCard')}</Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Grid>

        {showPreview && (
          <Grid size={{ xs: 12, lg: 5, xl: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <PhonePreview url={menu.preview_url} version={previewVersion} t={t} />
          </Grid>
        )}
      </Grid>

      {/* Save bar for design / info edits */}
      {showPreview && canManage && (
        <Paper elevation={8} sx={{ position: 'sticky', bottom: 16, mt: 3, p: 1.5, pl: 3, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2, zIndex: 5 }}>
          <Typography variant="body2" color={dirty ? 'text.primary' : 'text.secondary'} sx={{ flex: 1 }}>
            {dirty ? t('menus.unsavedChanges') : t('menus.allSaved')}
          </Typography>
          {dirty && (
            <Button startIcon={<RefreshIcon />} onClick={() => { setForm(toForm(menu)); setLogo(emptyImage(menu.logo_url)); setCover(emptyImage(menu.cover_url)); }}>
              {t('menus.discard')}
            </Button>
          )}
          <Button variant="contained" onClick={save} disabled={!dirty || saving}>{saving ? <CircularProgress size={20} /> : t('menus.saveChanges')}</Button>
        </Paper>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MenuEditor;
